import { VFC, Ref, useEffect, forwardRef, useMemo } from 'react';
import { ControlProps } from '@adsk/alloy-react-dropdown';
import Badge from '@adsk/alloy-react-badge';
import { RfiStatus, WorkflowType } from '../../../sdk';
import { CommonFormField, StyledBadge, SingleSelectDropdown } from '../../../components';
import {
  T,
  getStatusColor,
  getStatusLabel,
  getSelectedTransition,
  getStringifyStatusDropdownOptionFunc,
} from '../../../helpers';
import { useStatusOptions, StatusOption } from '../../../hooks/useStatusOptions';
import { useGetRfiTypes } from '../../../hooks';

type DropdownOption = { value: RfiStatus; label: '' };
export type PureStatusFieldProps = {
  required: boolean;
  isError?: boolean;
  ref?: Ref<HTMLDivElement>;
  value: RfiStatus;
  disabled?: boolean;
  rfiTypeId: string;
  onChange: (transition: StatusOption | undefined) => void;
};

const stringifyStatusDropdownOption = getStringifyStatusDropdownOptionFunc(true);

const renderStatusItem = (item: DropdownOption) => {
  const status = item.value;
  const { statusLabel, subStatusLabel } = getStatusLabel(status, true);
  return (
    <StyledBadge
      color={getStatusColor(status)}
      variant={Badge.VARIANTS.LINE}
      stacked={false}
      subStatus={subStatusLabel}
    >
      {statusLabel}
    </StyledBadge>
  );
};

const renderItem: ControlProps<DropdownOption>['renderItem'] = ({ selectedItem }) => {
  const selectedStatus = selectedItem?.value;
  if (!selectedStatus) {
    return null;
  }
  const { statusLabel, subStatusLabel } = getStatusLabel(selectedStatus, true);

  return (
    <StyledBadge
      color={getStatusColor(selectedStatus)}
      variant={Badge.VARIANTS.LINE}
      stacked={false}
      subStatus={subStatusLabel}
    >
      {statusLabel}
    </StyledBadge>
  );
};

export const PureStatusField: VFC<PureStatusFieldProps> = forwardRef(
  ({ required, isError = false, value, disabled, onChange, rfiTypeId }, ref) => {
    const { data: rfiTypes } = useGetRfiTypes();
    const selectedRfiType = rfiTypes?.find((rfiType) => rfiType.id === rfiTypeId);

    const statusOptions = useStatusOptions(selectedRfiType?.wfType as WorkflowType);

    // Update status field on load and on rfi type change
    useEffect(() => {
      if (statusOptions.length) {
        const selectedTransition = getSelectedTransition(statusOptions);
        onChange(selectedTransition);
      }
    }, [statusOptions, selectedRfiType?.id]);

    const dropdownOptions: DropdownOption[] = useMemo(
      () =>
        statusOptions.map(({ serverStatus }) => ({
          value: serverStatus,
          label: '',
        })),
      [statusOptions],
    );

    const handleSelectStatus = (serverStatus: RfiStatus) => {
      const newSelectedTransition = statusOptions.find(
        (transition) => transition.serverStatus === serverStatus,
      );
      onChange(newSelectedTransition);
    };

    return (
      <CommonFormField
        label={T.translate('new.status')}
        required={required}
        error={isError}
        ref={ref}
      >
        <SingleSelectDropdown<DropdownOption>
          options={dropdownOptions}
          value={value}
          disabled={disabled}
          onChange={handleSelectStatus}
          renderLeftComponent={renderStatusItem}
          renderItem={renderItem}
          itemToString={stringifyStatusDropdownOption}
        />
      </CommonFormField>
    );
  },
);
